export function GrossRentMultiplier() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Quick Valuation Method: Gross Rent Multiplier (GRM)
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            The Gross Rent Multiplier (GRM) is a simple ratio that provides a quick estimate of property value based on gross rental income. It's one of the fastest screening tools for apartment building valuation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">What Is Gross Rent Multiplier?</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The Gross Rent Multiplier (GRM) is the ratio of property price to gross annual rental income. It tells you how many years of gross rent it would take to pay for the property, providing a quick way to compare investment opportunities.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The formula works two ways: <strong>GRM = Property Price ÷ Gross Annual Rent</strong> (to find the multiplier), or <strong>Property Value = Gross Annual Rent × GRM</strong> (to estimate value). Lower GRMs indicate better value, while higher GRMs suggest premium pricing.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Key Formulas:</h4>
              <p className="text-gray-800 text-lg font-semibold mb-1">GRM = Property Price ÷ Gross Annual Rent</p>
              <p className="text-gray-800 text-lg font-semibold mb-2">Property Value = Gross Annual Rent × GRM</p>
              <p className="text-gray-600">The simplest income-based valuation metric</p>
            </div>
          </div>
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/dfa4708727df471d671197f23315488c.jpg"
              alt="Gross rent multiplier calculation for apartment buildings"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">How to Calculate and Use GRM</h3>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <span className="text-teal-600 font-bold text-2xl">1</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Calculate Gross Annual Rent</h4>
              <p className="text-gray-600 leading-relaxed">
                Multiply your monthly rental income by 12 to get gross annual rent. Include all rental income but not other income sources.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <span className="text-teal-600 font-bold text-2xl">2</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Find Market GRM</h4>
              <p className="text-gray-600 leading-relaxed">
                Research recent comparable sales and calculate their GRMs to determine the average market multiplier.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <span className="text-teal-600 font-bold text-2xl">3</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Estimate Your Value</h4>
              <p className="text-gray-600 leading-relaxed">
                Multiply your gross annual rent by the market GRM to get a quick property value estimate.
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 text-white">
              <h4 className="text-2xl font-bold mb-6 text-center">GRM Calculation Example</h4>
              
              <div className="space-y-6">
                <div className="bg-white/10 rounded-lg p-6">
                  <h5 className="text-xl font-bold mb-4">Step 1: Calculate Your Gross Annual Rent</h5>
                  <div className="space-y-2 text-lg">
                    <p>Monthly Rental Income: <strong>$24,000</strong></p>
                    <p>Gross Annual Rent: $24,000 × 12 = <strong>$288,000</strong></p>
                  </div>
                </div>

                <div className="bg-white/10 rounded-lg p-6">
                  <h5 className="text-xl font-bold mb-4">Step 2: Determine Market GRM from Comparables</h5>
                  <div className="space-y-2 text-base">
                    <p>Comp #1: $1,890,000 ÷ $180,000 = GRM of 10.5</p>
                    <p>Comp #2: $2,200,000 ÷ $216,000 = GRM of 10.2</p>
                    <p>Comp #3: $2,100,000 ÷ $204,000 = GRM of 10.3</p>
                    <p>Comp #4: $2,520,000 ÷ $240,000 = GRM of 10.5</p>
                    <p className="text-lg font-bold mt-3">Average Market GRM: <strong>10.4</strong></p>
                  </div>
                </div>

                <div className="bg-white/10 rounded-lg p-6 border-2 border-white/30">
                  <h5 className="text-xl font-bold mb-4">Step 3: Calculate Your Property Value</h5>
                  <div className="text-center">
                    <p className="text-lg mb-2">Gross Annual Rent × Market GRM</p>
                    <p className="text-3xl font-bold">$288,000 × 10.4 = $2,995,200</p>
                    <p className="text-base opacity-90 mt-3">Rounded to: <strong>$3,000,000</strong></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=real-estate-market-comparison-different-grm-ratios-investment-analysis-property-comparison-chart-market-metrics-financial-performance-indicators-comparative-analysis-professional-investment-evaluation&width=800&height=600&seq=grm-comparison-001&orientation=landscape"
              alt="Comparing GRM ratios across different properties"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Understanding GRM Ranges</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              GRM typically ranges from 8 to 15 for apartment buildings, depending on location, property quality, and market conditions. Understanding what different GRM levels indicate helps you evaluate investment opportunities.
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-green-500">
                <h4 className="font-bold text-gray-900 mb-2">GRM 8-10 (Lower)</h4>
                <p className="text-gray-600">Better value, higher returns, may indicate secondary markets or value-add opportunities</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-yellow-500">
                <h4 className="font-bold text-gray-900 mb-2">GRM 10-12 (Moderate)</h4>
                <p className="text-gray-600">Balanced pricing, typical for stable markets with moderate growth potential</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-orange-500">
                <h4 className="font-bold text-gray-900 mb-2">GRM 12-15 (Higher)</h4>
                <p className="text-gray-600">Premium pricing, prime locations, lower returns but lower risk and strong appreciation potential</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-red-500">
                <h4 className="font-bold text-gray-900 mb-2">GRM 15+ (Very High)</h4>
                <p className="text-gray-600">Expensive markets, may indicate overpricing or expectation of significant rent growth</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">GRM vs Cap Rate: Understanding the Difference</h3>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-teal-50 rounded-lg p-6 border-2 border-teal-600">
              <div className="text-center mb-4">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-600 rounded-full mx-auto mb-3">
                  <i className="ri-calculator-line text-white text-2xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900">Gross Rent Multiplier</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-1 flex-shrink-0"></i>
                  <span>Based on <strong>gross income</strong> (before expenses)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-1 flex-shrink-0"></i>
                  <span>Simpler calculation, faster screening</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-1 flex-shrink-0"></i>
                  <span>Doesn't account for operating expenses</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-1 flex-shrink-0"></i>
                  <span>Less accurate for detailed analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-1 flex-shrink-0"></i>
                  <span>Best for quick comparisons</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-1 flex-shrink-0"></i>
                  <span>Typical range: 8-15</span>
                </li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6 border-2 border-green-600">
              <div className="text-center mb-4">
                <div className="w-16 h-16 flex items-center justify-center bg-green-600 rounded-full mx-auto mb-3">
                  <i className="ri-percent-line text-white text-2xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900">Cap Rate</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>Based on <strong>net income</strong> (after expenses)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>More complex, requires expense data</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>Accounts for all operating expenses</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>More accurate for investment decisions</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>Industry standard for valuation</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>Typical range: 4-10%</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-gray-100 rounded-lg p-6 max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 text-center leading-relaxed">
              <strong>Key Insight:</strong> GRM is useful for quick screening, but cap rate is more reliable for serious valuation because it accounts for operating expenses. Two properties with identical gross rents can have very different values if one has much higher expenses than the other.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">When to Use GRM</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              GRM is most useful in specific situations where you need quick estimates or when detailed financial information isn't readily available. Here's when GRM works best:
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-flashlight-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Initial Screening</h4>
                  <p className="text-gray-600">Quickly evaluate multiple properties to narrow down options before detailed analysis</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-file-search-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Limited Information</h4>
                  <p className="text-gray-600">When you know gross rents but don't have detailed expense information yet</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-exchange-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Market Comparisons</h4>
                  <p className="text-gray-600">Comparing properties across different markets where expense ratios are similar</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-speed-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Quick Estimates</h4>
                  <p className="text-gray-600">Need a ballpark value estimate in minutes rather than hours</p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=real-estate-investor-quick-property-screening-multiple-listings-fast-analysis-investment-opportunities-property-comparison-efficient-evaluation-professional-investor-reviewing-deals-market-research&width=800&height=600&seq=quick-screening-001&orientation=landscape"
              alt="Quick property screening using GRM"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="mt-16 bg-yellow-50 border-l-4 border-yellow-500 p-8 rounded-r-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-full flex-shrink-0">
              <i className="ri-alert-line text-yellow-600 text-2xl"></i>
            </div>
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Important Limitations of GRM</h4>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                GRM's biggest weakness is that it ignores operating expenses entirely. Two properties with identical gross rents could have vastly different values if one has property taxes of $50,000 annually while the other has taxes of $150,000. GRM would value them identically, which would be incorrect.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Best Practice:</strong> Use GRM only for preliminary screening and quick estimates. Always confirm value using cap rate analysis and the income approach, which account for operating expenses and provide much more accurate valuations. Never make a purchase decision based solely on GRM.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}